import {
  type StockBucket,
  type StockItemSummary,
} from "@artisancode/api-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useStockReasons } from "@/hooks/use-stock-reasons";
import { newIdempotencyKey } from "@/lib/idempotency";
import { stockService } from "@/services/stock";
import { stockAdjustmentService } from "@/services/stock-adjustment";

type AdjustmentItemType = "raw_material" | "product";

const today = () => new Date().toISOString().slice(0, 10);

function bucketQty(item: StockItemSummary, bucket: StockBucket): number {
  if (bucket === "on_hand") return item.onHandQty;
  if (bucket === "repacking") return item.repackingQty;
  return item.riderQty;
}

/** State, system-vs-counted difference and submit logic for the stock adjustment dialog. */
export function useStockAdjustmentForm({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [date, setDate] = useState(today);
  const [itemType, setItemType] = useState<AdjustmentItemType>("raw_material");
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [bucket, setBucket] = useState<StockBucket>("on_hand");
  const [countedQtyStr, setCountedQtyStr] = useState<string>("");
  const [reasonId, setReasonId] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One key per opened dialog: a retry after a network error reuses it, so the
  // server returns the adjustment it already made instead of adjusting twice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey("sadj"), [open]);

  // Fetch stocks list
  const { data: stocksRes, isLoading: isLoadingStocks } = useQuery({
    queryKey: ["stocks", "all-items"],
    queryFn: () => stockService.list({ per_page: 500 }),
    enabled: open,
  });

  const allItems = useMemo(() => stocksRes?.items ?? [], [stocksRes]);
  const filteredItems = useMemo(
    () => allItems.filter((i) => i.itemType === itemType),
    [allItems, itemType],
  );

  const selectedItem: StockItemSummary | undefined = useMemo(
    () => filteredItems.find((i) => i.itemId === selectedItemId),
    [filteredItems, selectedItemId],
  );

  // Reset item selection when itemType changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedItemId("");
    setCountedQtyStr("");
    setReasonId("");
  }, [itemType]);

  // Determine system qty for selected bucket
  const systemQty = selectedItem ? bucketQty(selectedItem, bucket) : 0;

  // Counted qty & diff
  const countedQty = countedQtyStr === "" ? systemQty : Number(countedQtyStr);
  const difference = Number((countedQty - systemQty).toFixed(4));

  // Determine applicable direction
  const expectedDirection =
    difference > 0 ? "plus" : difference < 0 ? "minus" : "both";

  // Fetch reasons
  const { data: reasons = [] } = useStockReasons({ is_active: true });

  const applicableReasons = useMemo(() => {
    if (difference === 0) return reasons;
    return reasons.filter(
      (r) => r.direction === expectedDirection || r.direction === "both",
    );
  }, [reasons, difference, expectedDirection]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDate(today());
      setItemType("raw_material");
      setSelectedItemId("");
      setBucket("on_hand");
      setCountedQtyStr("");
      setReasonId("");
      setNote("");
    }
  }, [open]);

  // When item is selected, initialize countedQtyStr with systemQty
  const selectItem = (itemId: string) => {
    setSelectedItemId(itemId);
    if (!itemId) {
      setCountedQtyStr("");
      return;
    }
    const itm = filteredItems.find((i) => i.itemId === itemId);
    if (itm) setCountedQtyStr(String(bucketQty(itm, bucket)));
  };

  // When bucket changes, update countedQtyStr to current bucket balance
  const selectBucket = (newBucket: StockBucket) => {
    setBucket(newBucket);
    if (selectedItem) {
      setCountedQtyStr(String(bucketQty(selectedItem, newBucket)));
    }
  };

  const submit = async () => {
    if (!selectedItemId) {
      toast.error("Silakan pilih barang terlebih dahulu.");
      return;
    }
    if (isNaN(countedQty) || countedQty < 0) {
      toast.error("Jumlah fisik dihitung harus berupa angka positif.");
      return;
    }
    if (difference === 0) {
      toast.error("Tidak ada selisih. Jumlah fisik sama dengan saldo sistem.");
      return;
    }
    if (!reasonId) {
      toast.error("Pilih alasan penyesuaian stok.");
      return;
    }

    try {
      setIsSubmitting(true);
      await stockAdjustmentService.create(
        {
          date,
          itemType,
          itemId: selectedItemId,
          bucket,
          countedQty,
          reasonId,
          note: note.trim() || undefined,
        },
        idempotencyKey,
      );
      toast.success("Penyesuaian stok berhasil disimpan.");
      onSuccess();
      onOpenChange(false);
    } catch {
      // Error handled by queryClient / toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    date,
    setDate,
    itemType,
    setItemType,
    selectedItemId,
    bucket,
    countedQtyStr,
    setCountedQtyStr,
    reasonId,
    setReasonId,
    note,
    setNote,
    isSubmitting,
    isLoadingStocks,
    filteredItems,
    selectedItem,
    systemQty,
    difference,
    applicableReasons,
    selectItem,
    selectBucket,
    submit,
  };
}
